import React, { useState, useEffect } from 'react';
import '@css/index.scss';
import { getImage } from "@public/ulits";
import Footer from "@public/components/Footer";
import Header from "@components/Header";
import { api } from '@public/ulits/api';
import usePageLang from '@public/hooks/usePageLang';
import { t } from '@/locales';
import { Modal, Form, Input, Button, message, Spin } from 'antd';

const { TextArea } = Input;

export default () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [form] = Form.useForm();
    const [pageData, setPageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { pick } = usePageLang();

    useEffect(() => {
        api.getAgentPage()
            .then(res => {
                if (res && res.data) setPageData(res.data);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSubmit = (values) => {
        api.submitApplication(values)
            .then(() => {
                message.success(t('agent.submitSuccess'));
            })
            .catch(() => {
                message.success(t('agent.submitSuccess'));
            });
        form.resetFields();
        setModalOpen(false);
    };

    const bannerImage = pageData?.banner_image || getImage('banner2.jpg');
    const bannerTitle = pageData ? pick(pageData.banner_title_en, pageData.banner_title_zh) : '';
    const bannerSubtitle = pageData ? pick(pageData.banner_subtitle_en, pageData.banner_subtitle_zh) : '';

    const lookingTitle = pageData ? pick(pageData.looking_title_en, pageData.looking_title_zh) : '';
    const lookingContent = pageData ? pick(pageData.looking_content_en, pageData.looking_content_zh) : '';
    const lookingItems = lookingContent
        ? lookingContent.split('\n').filter(line => line.trim())
        : [];
    const lookingImage = pageData?.looking_image || getImage('image2.jpg');

    const offerTitle = pageData ? pick(pageData.offer_title_en, pageData.offer_title_zh) : '';
    const offerContent = pageData ? pick(pageData.offer_content_en, pageData.offer_content_zh) : '';
    const offerItems = offerContent
        ? offerContent.split('\n').filter(line => line.trim())
        : [];
    const offerImage = pageData?.offer_image || getImage('image1.jpg');

    const joinImage = pageData?.join_image || getImage('image3.jpg');
    const ctaImage = pageData?.cta_image || getImage('concat-image.png');
    const joinTitle = pageData ? pick(pageData.join_title_en, pageData.join_title_zh) : '';
    const joinContent = pageData ? pick(pageData.join_content_en, pageData.join_content_zh) : '';

    if (loading) {
        return (
            <div className="page-agent">
                <div className="w-full">
                    <Header />
                    <div className="flex justify-center py-40">
                        <Spin size="large" />
                    </div>
                    <Footer />
                </div>
            </div>
        );
    }

    return (
        <div className="page-agent">
            <div className="w-full">
                <Header />

                {/* Section 1: Banner */}
                <div className="agent-banner relative w-full lg:h-[680px] h-[480px] bg-cover bg-center"
                    style={{ backgroundImage: `url(${bannerImage})` }}>
                    <div className="absolute inset-0 bg-black/45 flex items-center justify-center pt-[0%] lg:pt-0">
                        <div className="text-center px-5">
                            <h1 className="text-white lg:text-[88px] text-2xl font-bold tracking-wide">{bannerTitle}</h1>
                            <p className="text-white/85 lg:text-[36px] text-base mt-2 lg:mt-10 tracking-wider">{bannerSubtitle}</p>
                        </div>
                    </div>
                </div>

                {/* Section 3: We Are Looking For */}
                <div className="agent-looking lg:w-[70%] lg:px-0 px-5 mx-auto lg:py-[0px] py-10">
                    <div className="flex flex-col lg:flex-row items-center gap-10">
                        <div className="lg:flex-1 w-full">
                            <h2 className="text-white lg:text-[40px] text-2xl font-bold mb-4 lg:mb-8">{lookingTitle}</h2>
                            <ul className="text-white/85 lg:text-xl text-base space-y-3 lg:space-y-6">
                                {lookingItems.map((item, index) => (
                                    <li key={index} className="flex items-center gap-4">
                                        <span className="agent-dot"></span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="lg:w-[50%] w-full">
                            <img src={lookingImage} alt={lookingTitle} className="w-full" />
                        </div>
                    </div>
                </div>

                {/* Section 2: What We Offer Our Agent */}
                <div className="agent-offer relative w-full lg:h-[620px] h-[450px] bg-cover bg-center"
                    style={{ backgroundImage: `url(${offerImage})` }}>
                    <div className="absolute inset-0 bg-black/35">
                        <div className="lg:w-[70%] mx-auto h-full flex items-center justify-end lg:px-0 px-5">
                            <div className="text-white text-left lg:w-[50%]">
                                <h2 className="lg:text-[40px] text-2xl font-bold mb-4 lg:mb-8">{offerTitle}</h2>
                                <ul className="lg:text-xl text-base space-y-2 lg:space-y-4 text-white/90">
                                    {offerItems.map((item, index) => (
                                        <li key={index} className="flex items-center gap-4"><span className="agent-dot"></span>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>



                {/* Section 4: Join Now */}
                <div className="agent-join lg:w-[70%] lg:px-0 px-5 mx-auto lg:py-[80px] py-10">
                    <div className="flex flex-col lg:flex-row items-center gap-10">
                        <div className="lg:flex-1 w-full">
                            {joinTitle && <h2 className="text-white lg:text-[40px] text-2xl font-bold mb-4 lg:mb-8">{joinTitle}</h2>}
                            {joinContent && (
                                <ul className="text-white/85 lg:text-xl text-base mb-6 lg:mb-10 space-y-3 lg:space-y-6">
                                    {joinContent.split('\n').filter(line => line.trim()).map((item, index) => (
                                        <li key={index} className="flex items-center gap-4">
                                            <span className="agent-dot"></span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <img
                                src={ctaImage}
                                alt="Contact"
                                onClick={() => setModalOpen(true)}
                                className="lg:w-[380px] w-[200px] cursor-pointer animate__animated animate__pulse animate__infinite"
                            />
                        </div>
                        <div className="lg:w-[50%] w-full">
                            <img src={joinImage} alt="Join Our Team" className="w-full" />
                        </div>
                    </div>
                </div>

                <Footer />

                {/* Application Form Modal */}
                <Modal
                    title={<span className="text-white text-xl">{t('agent.applicationForm')}</span>}
                    open={modalOpen}
                    onCancel={() => { setModalOpen(false); form.resetFields(); }}
                    footer={null}
                    centered
                    className="agent-modal"
                    width={520}
                >
                    <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
                        <Form.Item name="name" label={<span className="text-white/80">{t('agent.name')}</span>}
                            rules={[{ required: true, message: 'Please enter your name' }]}>
                            <Input placeholder={t('agent.namePlaceholder')} className="agent-input" />
                        </Form.Item>
                        <Form.Item name="phone" label={<span className="text-white/80">{t('agent.phone')}</span>}
                            rules={[{ required: true, message: 'Please enter your phone number' }]}>
                            <Input placeholder={t('agent.phonePlaceholder')} className="agent-input" />
                        </Form.Item>
                        <Form.Item name="email" label={<span className="text-white/80">{t('agent.email')}</span>}
                            rules={[
                                { required: true, message: 'Please enter your email' },
                                { type: 'email', message: 'Please enter a valid email' }
                            ]}>
                            <Input placeholder={t('agent.emailPlaceholder')} className="agent-input" />
                        </Form.Item>
                        <Form.Item name="message" label={<span className="text-white/80">{t('agent.message')}</span>}>
                            <TextArea rows={4} placeholder={t('agent.messagePlaceholder')} className="agent-input" />
                        </Form.Item>
                        <Form.Item className="mb-0 text-center">
                            <Button type="primary" htmlType="submit" size="large" className="agent-submit-btn">
                                {t('agent.submit')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </div>
    );
};
